import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import universityService from "../../services/universityService";
import countryService from "../../services/countryService";
import cityService from "../../services/cityService";

const CampusForm = ({ campus, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    universityId: "",
    nameEn: "",
    nameLocal: "",
    countryId: "",
    cityId: "",
  });

  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingUniversities, setLoadingUniversities] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [errors, setErrors] = useState({});

  // University search states
  const [universitySearchTerm, setUniversitySearchTerm] = useState("");
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [showUniversityDropdown, setShowUniversityDropdown] = useState(false);
  const [universitySearchTimeout, setUniversitySearchTimeout] = useState(null);

  // City search states
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [filteredCities, setFilteredCities] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [citySearchTimeout, setCitySearchTimeout] = useState(null);

  const { showError } = useNotification();

  useEffect(() => {
    fetchUniversities();
    fetchCountries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (campus) {
      setFormData({
        universityId: campus.universityId || "",
        nameEn: campus.nameEn || "",
        nameLocal: campus.nameLocal || "",
        countryId: campus.countryId || "",
        cityId: campus.cityId || "",
      });

      // Set university search term for display
      if (campus.universityName) {
        setUniversitySearchTerm(campus.universityName);
      }

      // Set city search term for display
      if (campus.city) {
        setCitySearchTerm(campus.city);
      }

      if (campus.countryId) {
        fetchCitiesByCountry(campus.countryId);
      }
    } else {
      setFormData({
        universityId: "",
        nameEn: "",
        nameLocal: "",
        countryId: "",
        cityId: "",
      });
      setUniversitySearchTerm("");
      setCitySearchTerm("");
    }
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campus]);

  useEffect(() => {
    if (formData.countryId) {
      fetchCitiesByCountry(formData.countryId);
    } else {
      setCities([]);
      setFilteredCities([]);
      setFormData((prev) => ({ ...prev, cityId: "" }));
      setCitySearchTerm("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.countryId]);

  // University search effect
  useEffect(() => {
    if (universitySearchTimeout) {
      clearTimeout(universitySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterUniversities();
    }, 300);

    setUniversitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [universitySearchTerm, universities]);

  // City search effect
  useEffect(() => {
    if (citySearchTimeout) {
      clearTimeout(citySearchTimeout);
    }

    const timeout = setTimeout(() => {
      filterCities();
    }, 300);

    setCitySearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySearchTerm, cities]);

  const fetchUniversities = async () => {
    try {
      setLoadingUniversities(true);
      const response = await universityService.getUniversities(0, 1000);
      if (response.success) {
        const sortedUniversities = (response.data.data || []).sort((a, b) =>
          (a.nameLocal || a.nameEn || "").localeCompare(
            b.nameLocal || b.nameEn || ""
          )
        );
        setUniversities(sortedUniversities);
        setFilteredUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error("Failed to fetch universities:", error);
      showError("Failed to load universities");
    } finally {
      setLoadingUniversities(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await countryService.getAllCountries();
      if (response.success) {
        setCountries(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch countries:", error);
      showError("Failed to load countries");
    } finally {
      setLoadingCountries(false);
    }
  };

  const fetchCitiesByCountry = async (countryId) => {
    try {
      setLoadingCities(true);
      const response = await cityService.getCitiesByCountry(countryId);
      if (response.success) {
        const sortedCities = (response.data || []).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        setCities(sortedCities);
        setFilteredCities(sortedCities);
      }
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      showError("Failed to load cities");
      setCities([]);
      setFilteredCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const filterUniversities = () => {
    if (!universitySearchTerm.trim()) {
      setFilteredUniversities(universities);
      return;
    }

    const filtered = universities.filter(
      (university) =>
        (university.nameEn || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase()) ||
        (university.nameLocal || "")
          .toLowerCase()
          .includes(universitySearchTerm.toLowerCase())
    );
    setFilteredUniversities(filtered);
  };

  const filterCities = () => {
    if (!citySearchTerm.trim()) {
      setFilteredCities(cities);
      return;
    }

    const filtered = cities.filter((city) =>
      (city.name || "").toLowerCase().includes(citySearchTerm.toLowerCase())
    );
    setFilteredCities(filtered);
  };

  const handleUniversitySelect = (university) => {
    setFormData((prev) => ({
      ...prev,
      universityId: university.id,
    }));
    setUniversitySearchTerm(university.nameEn);
    setShowUniversityDropdown(false);

    // Clear university error if any
    if (errors.universityId) {
      setErrors((prev) => ({
        ...prev,
        universityId: "",
      }));
    }
  };

  const handleUniversitySearchChange = (e) => {
    const value = e.target.value;
    setUniversitySearchTerm(value);
    setShowUniversityDropdown(true);

    if (!value.trim()) {
      setFilteredUniversities(universities);
      setFormData((prev) => ({
        ...prev,
        universityId: "",
      }));
    }
  };

  const handleUniversitySearchFocus = () => {
    setShowUniversityDropdown(true);
  };

  const handleUniversitySearchBlur = () => {
    setTimeout(() => setShowUniversityDropdown(false), 200);
  };

  const handleCitySelect = (city) => {
    setFormData((prev) => ({
      ...prev,
      cityId: city.id,
    }));
    setCitySearchTerm(city.name);
    setShowCityDropdown(false);

    // Clear city error if any
    if (errors.cityId) {
      setErrors((prev) => ({
        ...prev,
        cityId: "",
      }));
    }
  };

  const handleCitySearchChange = (e) => {
    const value = e.target.value;
    setCitySearchTerm(value);
    setShowCityDropdown(true);

    if (!value.trim()) {
      setFilteredCities(cities);
      setFormData((prev) => ({
        ...prev,
        cityId: "",
      }));
    }
  };

  const handleCitySearchFocus = () => {
    setShowCityDropdown(true);
  };

  const handleCitySearchBlur = () => {
    setTimeout(() => setShowCityDropdown(false), 200);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "countryId") {
      setFormData((prev) => ({
        ...prev,
        countryId: value,
        cityId: "",
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.universityId) {
      newErrors.universityId = "University is required";
    }
    if (!formData.nameEn.trim()) {
      newErrors.nameEn = "English name is required";
    }
    if (!formData.nameLocal.trim()) {
      newErrors.nameLocal = "Local name is required";
    }
    if (!formData.countryId) {
      newErrors.countryId = "Country is required";
    }
    if (!formData.cityId) {
      newErrors.cityId = "City is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError("Please fix the validation errors");
      return;
    }

    const submitData = {
      universityId: formData.universityId,
      nameEn: formData.nameEn.trim(),
      nameLocal: formData.nameLocal.trim(),
      countryId: parseInt(formData.countryId),
      cityId: parseInt(formData.cityId),
    };

    onSubmit(submitData);
  };

  const getSelectedUniversity = () => {
    return universities.find((u) => u.id === formData.universityId);
  };

  const getSelectedCountry = () => {
    return countries.find((c) => c.id === parseInt(formData.countryId));
  };

  const getSelectedCity = () => {
    return cities.find((c) => c.id === parseInt(formData.cityId));
  };

  return (
    <div className="campus-form-container">
      <form onSubmit={handleSubmit} className="campus-form" noValidate>
        {/* Progress Steps */}
        <div className="form-progress mb-4">
          <div className="progress-steps">
            <div
              className={`step ${
                formData.universityId ? "completed" : "active"
              }`}
            >
              <div className="step-icon">
                <i className="fas fa-university"></i>
              </div>
              <span className="step-label">University</span>
            </div>
            <div
              className={`step ${
                formData.nameEn && formData.nameLocal
                  ? "completed"
                  : formData.universityId
                  ? "active"
                  : ""
              }`}
            >
              <div className="step-icon">
                <i className="fas fa-building"></i>
              </div>
              <span className="step-label">Campus Info</span>
            </div>
            <div
              className={`step ${
                formData.countryId && formData.cityId
                  ? "completed"
                  : formData.nameEn && formData.nameLocal
                  ? "active"
                  : ""
              }`}
            >
              <div className="step-icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <span className="step-label">Location</span>
            </div>
          </div>
        </div>

        {/* University Selection */}
        <div className="form-section">
          <div className="section-header">
            <h6 className="section-title">
              <i className="fas fa-university text-primary me-2"></i>
              University Selection
            </h6>
            <p className="section-subtitle">
              Choose the university for this campus
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="universityId" className="form-label">
              University <span className="required">*</span>
            </label>
            <div className="position-relative">
              <div className="input-group">
                <input
                  type="text"
                  name="universityNameEn"
                  className={`form-control ${
                    errors.universityId ? "is-invalid" : ""
                  }`}
                  placeholder="Search universities..."
                  value={universitySearchTerm}
                  onChange={handleUniversitySearchChange}
                  onFocus={handleUniversitySearchFocus}
                  onBlur={handleUniversitySearchBlur}
                  disabled={loading || loadingUniversities}
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() =>
                    setShowUniversityDropdown(!showUniversityDropdown)
                  }
                  disabled={loading || loadingUniversities}
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>

              {/* Hidden input for form validation */}
              <input
                type="hidden"
                name="universityId"
                value={formData.universityId}
              />

              {errors.universityId && (
                <div className="invalid-feedback">{errors.universityId}</div>
              )}
              {loadingUniversities && (
                <small className="text-muted">Loading universities...</small>
              )}

              {/* Dropdown for university selection */}
              {showUniversityDropdown && filteredUniversities.length > 0 && (
                <div className="university-dropdown">
                  {filteredUniversities.map((university) => (
                    <button
                      key={university.id}
                      type="button"
                      className="university-dropdown-item"
                      onClick={() => handleUniversitySelect(university)}
                    >
                      <div className="university-name-en">
                        {university.nameEn}
                      </div>
                      <div className="university-name-local">
                        {university.nameLocal}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showUniversityDropdown && filteredUniversities.length === 0 && (
                <div className="university-dropdown">
                  <div className="university-dropdown-item text-muted">
                    No universities found
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Campus Information */}
        <div className="form-section">
          <div className="section-header">
            <h6 className="section-title">
              <i className="fas fa-building text-success me-2"></i>
              Campus Information
            </h6>
            <p className="section-subtitle">
              Enter campus name in both languages
            </p>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="nameEn" className="form-label">
                  Campus Name (English) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.nameEn ? "is-invalid" : ""
                  }`}
                  id="nameEn"
                  name="nameEn"
                  value={formData.nameEn}
                  onChange={handleInputChange}
                  placeholder="Enter campus name in English"
                  disabled={loading}
                />
                {errors.nameEn && (
                  <div className="form-error">{errors.nameEn}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="nameLocal" className="form-label">
                  Campus Name (Local) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.nameLocal ? "is-invalid" : ""
                  }`}
                  id="nameLocal"
                  name="nameLocal"
                  value={formData.nameLocal}
                  onChange={handleInputChange}
                  placeholder="Enter campus name in local language"
                  disabled={loading}
                />
                {errors.nameLocal && (
                  <div className="form-error">{errors.nameLocal}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Location Selection */}
        <div className="form-section">
          <div className="section-header">
            <h6 className="section-title">
              <i className="fas fa-map-marker-alt text-info me-2"></i>
              Location Details
            </h6>
            <p className="section-subtitle">
              Select country and city for the campus
            </p>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="countryId" className="form-label">
                  Country <span className="required">*</span>
                </label>
                <div className="select-wrapper">
                  <select
                    className={`form-control form-select ${
                      errors.countryId ? "is-invalid" : ""
                    }`}
                    id="countryId"
                    name="countryId"
                    value={formData.countryId}
                    onChange={handleInputChange}
                    disabled={loading || loadingCountries}
                  >
                    <option value="">Select a country...</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {loadingCountries && (
                    <div className="loading-indicator">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                  )}
                </div>
                {errors.countryId && (
                  <div className="form-error">{errors.countryId}</div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="cityId" className="form-label">
                  City <span className="required">*</span>
                </label>
                <div className="position-relative">
                  <div className="input-group">
                    <input
                      type="text"
                      name="cityNameEn"
                      className={`form-control ${
                        errors.cityId ? "is-invalid" : ""
                      }`}
                      placeholder="Search cities..."
                      value={citySearchTerm}
                      onChange={handleCitySearchChange}
                      onFocus={handleCitySearchFocus}
                      onBlur={handleCitySearchBlur}
                      disabled={loading || loadingCities || !formData.countryId}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setShowCityDropdown(!showCityDropdown)}
                      disabled={loading || loadingCities || !formData.countryId}
                    >
                      <i className="fas fa-search"></i>
                    </button>
                  </div>

                  {/* Hidden input for form validation */}
                  <input type="hidden" name="cityId" value={formData.cityId} />

                  {errors.cityId && (
                    <div className="invalid-feedback">{errors.cityId}</div>
                  )}
                  {loadingCities && (
                    <small className="text-muted">Loading cities...</small>
                  )}
                  {!formData.countryId && (
                    <small className="text-muted">
                      Please select a country first
                    </small>
                  )}

                  {/* Dropdown for city selection */}
                  {showCityDropdown && filteredCities.length > 0 && (
                    <div className="university-dropdown">
                      {filteredCities.map((city) => (
                        <button
                          key={city.id}
                          type="button"
                          className="university-dropdown-item"
                          onClick={() => handleCitySelect(city)}
                        >
                          <div className="university-name-en">{city.name}</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {showCityDropdown && filteredCities.length === 0 && (
                    <div className="university-dropdown">
                      <div className="university-dropdown-item text-muted">
                        No cities found
                      </div>
                    </div>
                  )}
                </div>
                {!formData.countryId && (
                  <div className="form-help">
                    <i className="fas fa-info-circle me-1"></i>
                    Please select a country first to load available cities
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        {(formData.nameEn ||
          formData.nameLocal ||
          getSelectedUniversity() ||
          getSelectedCountry() ||
          getSelectedCity()) && (
          <div className="form-preview">
            <div className="preview-header">
              <h6 className="preview-title">
                <i className="fas fa-eye me-2"></i>
                Preview
              </h6>
            </div>
            <div className="preview-content">
              <div className="row">
                <div className="col-md-8">
                  <div className="preview-details">
                    <div className="preview-item">
                      <span className="preview-label">University:</span>
                      <span className="preview-value">
                        {getSelectedUniversity()?.nameEn || "Not selected"}
                      </span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Campus (EN):</span>
                      <span className="preview-value">
                        {formData.nameEn || "Campus Name (English)"}
                      </span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Campus (Local):</span>
                      <span className="preview-value">
                        {formData.nameLocal || "Campus Name (Local)"}
                      </span>
                    </div>
                    <div className="preview-item">
                      <span className="preview-label">Location:</span>
                      <span className="preview-value">
                        {getSelectedCity()?.name && getSelectedCountry()?.name
                          ? `${getSelectedCity().name}, ${
                              getSelectedCountry().name
                            }`
                          : "Not selected"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="preview-icon">
                    <div className="campus-preview-icon">
                      <i className="fas fa-building"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            <i className="fas fa-times me-2"></i>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || loadingUniversities || loadingCountries}
          >
            {loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                {campus ? "Updating..." : "Creating..."}
              </>
            ) : (
              <>
                <i className={`fas ${campus ? "fa-save" : "fa-plus"} me-2`}></i>
                {campus ? "Update Campus" : "Create Campus"}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CampusForm;
